import React, { useEffect, useRef } from 'react';

import css from './SectionIntelligenz.module.css';

/**
 * FairwayBid – der Schwarm im Hintergrund
 *
 * Ein Netz aus Punkten, die sich wie ein Vogelschwarm bewegen: Jeder Punkt
 * hält Abstand zu seinen Nachbarn, richtet sich nach ihrer Richtung und
 * strebt zu ihrer Mitte. Aus diesen drei Regeln entsteht das, was man auf dem
 * Feld über einem Stoppelacker sieht – eine Bewegung, die niemand vorgibt.
 *
 * Warum genau dieses Bild: Es ist dasselbe Prinzip wie unter der Oberfläche.
 * Eine Anfrage und ein Anbieter finden nicht deshalb zusammen, weil jemand
 * sie zusammenführt, sondern weil ein paar einfache Regeln auf viele
 * Beteiligte wirken. Ein beliebiges Partikelfeld wäre Dekoration; das hier
 * zeigt, wovon der Abschnitt spricht.
 *
 * Die Linien entstehen zwischen Punkten, die nahe genug beieinander sind, und
 * verblassen mit dem Abstand. Sie sind der eigentliche Effekt: Der Schwarm
 * knüpft sich beim Fliegen selbst.
 *
 * Was hier Rücksicht kostet
 * -------------------------
 *   - Gezeichnet wird auf Canvas, nicht mit hundert DOM-Knoten. Hundert
 *     Elemente mit Schatten bringen jedes Telefon zum Stocken.
 *   - Die Schleife hält an, sobald der Abschnitt aus dem Bild scrollt oder
 *     der Tab in den Hintergrund geht. Ein Hintergrundbild, das den Akku
 *     leert, während niemand hinsieht, ist keine Gestaltung, sondern ein
 *     Fehler.
 *   - Wer Bewegung abgestellt hat, bekommt ein einziges, stehendes Bild:
 *     dasselbe Netz, nur ohne Flug.
 *   - Die Punktzahl richtet sich nach der Fläche. Auf dem Telefon sind es
 *     wenige und große, auf dem Schreibtisch viele und feine - sonst ist es
 *     klein entweder leer oder Brei.
 */

// Die drei Regeln, mit denen ein Schwarm entsteht.
const REGELN = {
  abstand: 26, // darunter weichen sie einander aus
  sichtweite: 92, // so weit schaut ein Punkt nach Nachbarn
  linie: 124, // bis hierhin wird eine Linie gezogen
  tempo: 0.42,
  ausweichen: 0.055,
  ausrichten: 0.035,
  zusammen: 0.0016,
};

const punktzahl = (breite, hoehe) => {
  const flaeche = breite * hoehe;
  return Math.max(28, Math.min(110, Math.round(flaeche / 13000)));
};

const Schwarm = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const ruhe =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let breite = 0;
    let hoehe = 0;
    let punkte = [];
    let laeuft = false;
    let bild = 0;

    const messen = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const kasten = canvas.getBoundingClientRect();
      breite = Math.max(1, Math.round(kasten.width));
      hoehe = Math.max(1, Math.round(kasten.height));
      canvas.width = Math.round(breite * dpr);
      canvas.height = Math.round(hoehe * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const saeen = () => {
      const anzahl = punktzahl(breite, hoehe);
      punkte = Array.from({ length: anzahl }, () => {
        const richtung = Math.random() * Math.PI * 2;
        return {
          x: Math.random() * breite,
          y: Math.random() * hoehe,
          vx: Math.cos(richtung) * REGELN.tempo,
          vy: Math.sin(richtung) * REGELN.tempo,
        };
      });
    };

    const rechnen = () => {
      for (let i = 0; i < punkte.length; i += 1) {
        const p = punkte[i];
        let ausX = 0;
        let ausY = 0;
        let richtX = 0;
        let richtY = 0;
        let mittelX = 0;
        let mittelY = 0;
        let nachbarn = 0;

        for (let j = 0; j < punkte.length; j += 1) {
          if (i === j) continue;
          const q = punkte[j];
          const dx = q.x - p.x;
          const dy = q.y - p.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > REGELN.sichtweite * REGELN.sichtweite) continue;

          nachbarn += 1;
          richtX += q.vx;
          richtY += q.vy;
          mittelX += q.x;
          mittelY += q.y;

          if (d2 < REGELN.abstand * REGELN.abstand && d2 > 0) {
            const d = Math.sqrt(d2);
            ausX -= dx / d;
            ausY -= dy / d;
          }
        }

        if (nachbarn > 0) {
          p.vx += (richtX / nachbarn - p.vx) * REGELN.ausrichten;
          p.vy += (richtY / nachbarn - p.vy) * REGELN.ausrichten;
          p.vx += (mittelX / nachbarn - p.x) * REGELN.zusammen;
          p.vy += (mittelY / nachbarn - p.y) * REGELN.zusammen;
        }
        p.vx += ausX * REGELN.ausweichen;
        p.vy += ausY * REGELN.ausweichen;

        // Tempo begrenzen, sonst schaukelt sich der Schwarm auf und
        // verlaesst das Bild wie eine Rakete.
        const v = Math.hypot(p.vx, p.vy) || 1;
        const grenze = REGELN.tempo * 1.6;
        if (v > grenze) {
          p.vx = (p.vx / v) * grenze;
          p.vy = (p.vy / v) * grenze;
        }

        p.x += p.vx;
        p.y += p.vy;

        // Am Rand nicht abprallen, sondern durchfliegen: Ein Schwarm, der
        // an einer unsichtbaren Wand umkehrt, sieht nach Aquarium aus.
        if (p.x < -20) p.x = breite + 20;
        if (p.x > breite + 20) p.x = -20;
        if (p.y < -20) p.y = hoehe + 20;
        if (p.y > hoehe + 20) p.y = -20;
      }
    };

    const zeichnen = () => {
      ctx.clearRect(0, 0, breite, hoehe);

      ctx.lineWidth = 1;
      for (let i = 0; i < punkte.length; i += 1) {
        const p = punkte[i];
        for (let j = i + 1; j < punkte.length; j += 1) {
          const q = punkte[j];
          const dx = q.x - p.x;
          const dy = q.y - p.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > REGELN.linie * REGELN.linie) continue;
          const naehe = 1 - Math.sqrt(d2) / REGELN.linie;
          ctx.strokeStyle = `rgba(122, 214, 160, ${(naehe * 0.34).toFixed(3)})`;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(q.x, q.y);
          ctx.stroke();
        }
      }

      ctx.fillStyle = 'rgba(180, 235, 205, 0.8)';
      punkte.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.7, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const schritt = () => {
      if (!laeuft) return;
      rechnen();
      zeichnen();
      bild = requestAnimationFrame(schritt);
    };

    const anhalten = () => {
      laeuft = false;
      cancelAnimationFrame(bild);
    };

    const starten = () => {
      if (laeuft || ruhe) return;
      laeuft = true;
      bild = requestAnimationFrame(schritt);
    };

    messen();
    saeen();

    if (ruhe) {
      // Ein stehendes Bild braucht trotzdem eine Ordnung - frisch gesaet
      // sind die Punkte zufaellig verstreut. Ein paar Schritte rechnen,
      // dann einmal zeichnen.
      for (let i = 0; i < 140; i += 1) rechnen();
      zeichnen();
    }

    const sichtbar =
      typeof IntersectionObserver === 'function'
        ? new IntersectionObserver(
            eintraege => {
              const drin = eintraege.some(e => e.isIntersecting);
              if (drin) starten();
              else anhalten();
            },
            { threshold: 0 }
          )
        : null;

    if (sichtbar) sichtbar.observe(canvas);
    else starten();

    const beiTabwechsel = () => {
      if (document.hidden) anhalten();
      else starten();
    };
    document.addEventListener('visibilitychange', beiTabwechsel);

    let messTimer = null;
    const beiGroesse = () => {
      clearTimeout(messTimer);
      messTimer = setTimeout(() => {
        messen();
        saeen();
        if (ruhe) {
          for (let i = 0; i < 140; i += 1) rechnen();
          zeichnen();
        }
      }, 180);
    };
    window.addEventListener('resize', beiGroesse);

    return () => {
      anhalten();
      clearTimeout(messTimer);
      if (sichtbar) sichtbar.disconnect();
      document.removeEventListener('visibilitychange', beiTabwechsel);
      window.removeEventListener('resize', beiGroesse);
    };
  }, []);

  return <canvas ref={canvasRef} className={css.schwarm} aria-hidden="true" />;
};

export default Schwarm;
